import { useState, useEffect, useRef, useCallback } from 'react';
import { getApiBaseUrl } from '../../../../core/config/api';
import { Batiment, Personnel, Vehicule } from '../../../../shared/types';

export type ContextMenuState = { x: number; y: number; lat: number; lng: number } | null;

interface UseMapDataParams {
  currentUser: { id: number } | null;
  setContextMenu: React.Dispatch<React.SetStateAction<ContextMenuState>>;
}

export function useMapData({ currentUser, setContextMenu }: UseMapDataParams) {
  const [buildings, setBuildings] = useState<Batiment[]>([]);
  const [playerVehicles, setPlayerVehicles] = useState<Vehicule[]>([]);
  const [playerPersonnel, setPlayerPersonnel] = useState<Personnel[]>([]);
  const [loading, setLoading] = useState(true);
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const saveBuildings = useCallback(
    async (buildingsToSave: Batiment[]) => {
      if (!currentUser) return;
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        if (saveTimeoutRef.current) {
          clearTimeout(saveTimeoutRef.current);
          saveTimeoutRef.current = null;
        }
        const response = await fetch(`${getApiBaseUrl(true)}/buildings/save`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ batiments: buildingsToSave }),
        });
        if (!response.ok) {
          console.error('Erreur sauvegarde bâtiments:', response.status, response.statusText);
        }
      } catch (error) {
        console.error('Erreur lors de la sauvegarde automatique:', error);
      }
    },
    [currentUser],
  );

  useEffect(() => {
    const loadAll = async () => {
      if (!currentUser) {
        setLoading(false);
        return;
      }
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          setLoading(false);
          return;
        }
        const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
        const [buildRes, vehRes, persRes] = await Promise.all([
          fetch(`${getApiBaseUrl(true)}/buildings`, { headers }),
          fetch(`${getApiBaseUrl(true)}/buildings/vehicules`, { headers }),
          fetch(`${getApiBaseUrl(true)}/buildings/personnel`, { headers }),
        ]);

        if (buildRes.ok) {
          const data = await buildRes.json();
          setBuildings(data.success && data.data ? (Array.isArray(data.data) ? data.data : []) : []);
        } else {
          setBuildings([]);
        }
        if (vehRes.ok) {
          const d = await vehRes.json().catch(() => ({}));
          if (d?.success && Array.isArray(d?.data)) setPlayerVehicles(d.data as Vehicule[]);
        }
        if (persRes.ok) {
          const d = await persRes.json().catch(() => ({}));
          if (d?.success && Array.isArray(d?.data)) setPlayerPersonnel(d.data as Personnel[]);
        }
      } catch (error: any) {
        console.error('Erreur réseau chargement bâtiments:', error);
        setBuildings([]);
      } finally {
        setLoading(false);
      }
    };

    const handleMapContextMenu = (e: CustomEvent) => {
      setContextMenu({ x: e.detail.x, y: e.detail.y, lat: e.detail.lat, lng: e.detail.lng });
    };

    void loadAll();
    window.addEventListener('mapcontextmenu', handleMapContextMenu as EventListener);
    return () => window.removeEventListener('mapcontextmenu', handleMapContextMenu as EventListener);
  }, [currentUser, setContextMenu]);

  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    };
  }, []);

  return { buildings, setBuildings, playerVehicles, playerPersonnel, loading, saveBuildings };
}
